import { useState, useEffect } from 'react';
import axiosInstance from "../utils/AxiosInstance";
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import RansomwareVictimCharts from '../components/RansomwareVictimCharts';

export default function RansomwareVictims() {
  const [victims, setVictims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState('card');
  const [resultsPerPage, setResultsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('date_reported'); // default sort field
  const [sortAsc, setSortAsc] = useState(false);
  const [showStats, setShowStats] = useState(true);
  

  useEffect(() => {
    async function fetchVictims() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.append('search', search.trim());
        params.append('page', currentPage);
        params.append('page_size', resultsPerPage);
        // Map frontend sort field to backend field name
        const fieldMap = {
          date_reported: 'cyber_attack__date_reported',
          date_occurred: 'cyber_attack__date_occurred',
          group_name: 'group__name',
        };

        const orderingField = fieldMap[sortField] || sortField;
        params.append('ordering', (sortAsc ? '' : '-') + orderingField);

        const response = await axiosInstance.get(`/insights/ransomware-victims/?${params.toString()}`);

        const data = response.data;
        const filteredVictims = (data.results || []).filter(
          (v) => v.victim_name && !v.victim_name.includes('*')
        );

        // Sort victims based on field and direction
        const sorted = [...filteredVictims].sort((a, b) => {
          let aVal, bVal;

          if (sortField === 'date_occurred' || sortField === 'date_reported') {
            aVal = a.cyber_attack?.[sortField] ? new Date(a.cyber_attack[sortField]) : new Date(0);
            bVal = b.cyber_attack?.[sortField] ? new Date(b.cyber_attack[sortField]) : new Date(0);
          } else {
            aVal = (a[sortField] || '').toString().toLowerCase();
            bVal = (b[sortField] || '').toString().toLowerCase();
          }

          if (aVal < bVal) return sortAsc ? -1 : 1;
          if (aVal > bVal) return sortAsc ? 1 : -1;
          return 0;
        });

        setVictims(sorted);
        setTotalPages(Math.max(1, Math.ceil(data.count / resultsPerPage)));
      } catch (error) {
        console.error('Failed to fetch ransomware victims:', error);
        setVictims([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    }
    fetchVictims();
  }, [search, currentPage, resultsPerPage, sortField, sortAsc]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleLayoutChange = (newLayout) => setLayout(newLayout);
  const handleResultsPerPageChange = (e) => {
    setResultsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const sortableFields = [
    { key: 'victim_name', label: 'Victim Name' },
    { key: 'group_name', label: 'Ransomware Group' },
    { key: 'country', label: 'Country' },
    { key: 'sector', label: 'Sector' },
    { key: 'payment_status', label: 'Payment Status' },
    { key: 'date_occurred', label: 'Attack Date' },
    { key: 'date_reported', label: 'Reported Date' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
        <input
          type="text"
          placeholder="Search victims by name, country, group, or sector"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:w-1/3 p-2 rounded bg-gray-800 text-white border border-gray-700"
          aria-label="Search victims"
        />

        <div className="flex items-center gap-4">
          <button
            onClick={() => handleLayoutChange('card')}
            className={`p-2 rounded-full ${layout === 'card' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400'}`}
            aria-label="Card view"
            title="Card view"
          >
            <ViewColumnsIcon className="h-6 w-6" />
          </button>
          <button
            onClick={() => handleLayoutChange('table')}
            className={`p-2 rounded-full ${layout === 'table' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400'}`}
            aria-label="Table view"
            title="Table view"
          >
            <ViewColumnsIcon className="h-6 w-6" />
          </button>

          <select
            value={resultsPerPage}
            onChange={handleResultsPerPageChange}
            className="bg-gray-800 text-gray-400 border border-gray-700 rounded-md p-2"
            aria-label="Results per page"
          >
            {[5, 10, 15, 20].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-between items-center mt-6 mb-4">
        <h2 className="text-lg font-semibold text-purple-500">Ransomware Statistics</h2>
        <button
            onClick={() => setShowStats(!showStats)}
            className="text-sm text-purple-600 bg-gray-200 dark:bg-gray-800 dark:text-purple-400 border border-purple-500 dark:border-purple-600 px-3 py-1 rounded hover:bg-purple-100 dark:hover:bg-gray-700 transition"
        >
            {showStats ? "Hide Stats" : "Show Stats"}
        </button>
        </div>
      <p className="text-gray-400 mb-4 text-sm font-semibold">Source: Ransomware.live</p>
      {showStats && <RansomwareVictimCharts />}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && victims.length === 0 && (
        <p className="text-gray-400 text-center">No ransomware victims found.</p>
      )}

      {layout === 'card' && !loading && victims.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {victims.map((v) => (
            <div
              key={v.id}
              className="bg-gray-900 border border-gray-700 rounded-xl p-4 hover:shadow-lg transition group flex flex-col justify-between"
            >
              <div className="mb-2">
                <h2 className="text-lg font-semibold text-purple-300 group-hover:underline break-words">
                  {v.victim_name}
                </h2>
                <p className="text-gray-400 text-sm mb-1"><strong>Group:</strong> {v.group?.name || 'N/A'}</p>
                <p className="text-gray-400 text-sm mb-1"><strong>Country:</strong> {v.country || 'N/A'}</p>
                <p className="text-gray-400 text-sm mb-1"><strong>Sector:</strong> {v.sector || 'N/A'}</p>
                <p className="text-gray-400 text-sm mb-1"><strong>Payment Status:</strong> {v.payment_status || 'Unknown'}</p>

                {v.cyber_attack && (
                  <>
                    <p className="text-gray-400 text-sm mb-1">
                      <strong>Attack Date:</strong> {v.cyber_attack.date_occurred
                        ? new Date(v.cyber_attack.date_occurred).toLocaleDateString()
                        : 'N/A'}
                    </p>
                    <p className="text-gray-400 text-sm mb-2">
                      <strong>Reported Date:</strong> {v.cyber_attack.date_reported
                        ? new Date(v.cyber_attack.date_reported).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </>
                )}

                <p className="text-gray-400 text-sm mb-2 line-clamp-3 break-words">
                  {v.notes || 'No additional notes.'}
                </p>
              </div>

              {v.victim_url && (
                <a
                  href={v.victim_url.startsWith('http') ? v.victim_url : `https://${v.victim_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-300 hover:underline text-sm mt-auto"
                >
                  Visit Victim Site
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {layout === 'table' && !loading && victims.length > 0 && (
        <div className="overflow-x-auto w-full">
          <table className="min-w-full table-auto bg-gray-900 border border-gray-700 rounded-xl text-sm">
            <thead>
              <tr className="text-left text-purple-300">
                {sortableFields.map(({ key, label }) => (
                  <th
                    key={key}
                    className="py-2 px-3 border-b border-gray-700 cursor-pointer hover:underline select-none"
                    onClick={() => toggleSort(key)}
                    aria-sort={
                      sortField === key ? (sortAsc ? 'ascending' : 'descending') : 'none'
                    }
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleSort(key);
                    }}
                    role="columnheader"
                  >
                    {label} {sortField === key && (sortAsc ? '▲' : '▼')}
                  </th>
                ))}
                <th className="py-2 px-3 border-b border-gray-700">Notes</th>
                <th className="py-2 px-3 border-b border-gray-700">Site</th>
              </tr>
            </thead>
            <tbody>
              {victims.map((v) => (
                <tr key={v.id} className="text-gray-400 hover:bg-gray-800 align-top text-sm">
                  <td className="py-2 px-3 border-b border-gray-700 max-w-xs break-words">{v.victim_name}</td>
                  <td className="py-2 px-3 border-b border-gray-700">{v.group?.name || 'N/A'}</td>
                  <td className="py-2 px-3 border-b border-gray-700">{v.country || 'N/A'}</td>
                  <td className="py-2 px-3 border-b border-gray-700">{v.sector || 'N/A'}</td>
                  <td className="py-2 px-3 border-b border-gray-700">{v.payment_status || 'Unknown'}</td>
                  <td className="py-2 px-3 border-b border-gray-700">
                    {v.cyber_attack?.date_occurred
                      ? new Date(v.cyber_attack.date_occurred).toLocaleString()
                      : 'N/A'}
                  </td>
                  <td className="py-2 px-3 border-b border-gray-700">
                    {v.cyber_attack?.date_reported
                      ? new Date(v.cyber_attack.date_reported).toLocaleString()
                      : 'N/A'}
                  </td>
                  <td className="py-2 px-3 border-b border-gray-700 max-w-xs overflow-hidden">
                    <div className="line-clamp-3">{v.notes || 'No notes'}</div>
                  </td>
                  <td className="py-2 px-3 border-b border-gray-700">
                    {v.victim_url ? (
                      <a
                        href={v.victim_url.startsWith('http') ? v.victim_url : `https://${v.victim_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-300 hover:underline"
                      >
                        Visit
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-4">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="px-4 py-2 bg-purple-600 text-white rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="px-4 py-2 text-gray-400">{`Page ${currentPage} of ${totalPages}`}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="px-4 py-2 bg-purple-600 text-white rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
